import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { http, inr, computeTotals, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, ScanLine } from "lucide-react";
import { useItemConfig } from "@/components/ItemConfig";
import { toast } from "sonner";
import { resolveDocumentText } from "@/lib/documentSettings";

const ICOLS = [
  { key: "hsn", label: "HSN" }, { key: "batch_no", label: "Batch" }, { key: "expiry_date", label: "Expiry" },
  { key: "qty", label: "Qty" }, { key: "free_qty", label: "Free" }, { key: "rate", label: "Rate" },
  { key: "discount_pct", label: "Disc%" }, { key: "gst_rate", label: "GST%" },
];

const newItem = () => ({ product_id: "", name: "", hsn: "", batch_no: "", expiry_date: "", unit: "PCS", qty: 1, free_qty: 0, rate: 0, mrp: 0, discount_pct: 0, gst_rate: 12 });
const savedBankLabel = (details) => [
  "Saved on this bill",
  details?.bank_name || details?.name,
  details?.account_number || details?.bank_account || details?.account,
  details?.ifsc || details?.bank_ifsc,
].filter(Boolean).join(" · ");

export default function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = !!id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [posCode, setPosCode] = useState("36");
  const [posName, setPosName] = useState("Telangana");
  const [invDate, setInvDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [items, setItems] = useState([newItem()]);
  const [shipping, setShipping] = useState(0);
  const [other, setOther] = useState(0);
  const [tcs, setTcs] = useState(0);
  const [tds, setTds] = useState(0);
  const [roundOff, setRoundOff] = useState(true);
  const [received, setReceived] = useState(0);
  const [payMode, setPayMode] = useState("Cash");
  const [notes, setNotes] = useState("");
  const [company, setCompany] = useState({});
  const [document, setDocument] = useState(null);
  const [bankId, setBankId] = useState("__none__");
  const [originalBankSelection, setOriginalBankSelection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  const [priceListId, setPriceListId] = useState("");
  const [plRates, setPlRates] = useState({});
  const [priceRatesLoading, setPriceRatesLoading] = useState(false);
  const [priceRatesError, setPriceRatesError] = useState("");
  const [priceRatesRetry, setPriceRatesRetry] = useState(0);
  const [scan, setScan] = useState("");
  const [prereqLoading, setPrereqLoading] = useState(true);
  const [prereqError, setPrereqError] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");
  const [documentLoading, setDocumentLoading] = useState(!!editing);
  const [documentError, setDocumentError] = useState("");
  const cfg = useItemConfig("invoice", "invoice", ICOLS);

  const loadPrerequisites = async () => {
    setPrereqLoading(true); setPrereqError("");
    try {
      const [customerRes, productRes] = await Promise.all([http.get("/customers"), http.get("/products")]);
      setCustomers(customerRes.data); setProducts(productRes.data);
    } catch (e) {
      setPrereqError(requestErrorMessage(e, "load customers and products"));
    } finally { setPrereqLoading(false); }
    try { const r = await http.get("/masters/price_lists"); setPriceLists(r.data); }
    catch (e) { toast.warning(requestErrorMessage(e, "load price lists")); }
  };
  const loadSettings = async () => {
    setSettingsLoading(true); setSettingsError("");
    try {
      const { data } = await http.get("/settings");
      setCompany(data || {});
      if (!editing) {
        const banks = data?.banks || [];
        const defaultBank = banks.find((bank) => String(bank.id) === String(data?.default_bank_id));
        setBankId(defaultBank ? String(defaultBank.id) : "__none__");
      }
    } catch (e) { setSettingsError(requestErrorMessage(e, "load company settings")); }
    finally { setSettingsLoading(false); }
  };
  useEffect(() => { loadPrerequisites(); loadSettings(); }, [editing]);

  useEffect(() => {
    if (!priceListId) {
      setPlRates({}); setPriceRatesError(""); setPriceRatesLoading(false);
      return undefined;
    }
    let active = true;
    setPriceRatesLoading(true); setPriceRatesError("");
    http.get(`/price-lists/${priceListId}/items`).then((r) => {
      if (!active) return;
      const m = {}; r.data.forEach((x) => { m[x.product_id] = x.price; }); setPlRates(m);
    }).catch((e) => {
      if (active) setPriceRatesError(requestErrorMessage(e, "load selected price list rates"));
    }).finally(() => {
      if (active) setPriceRatesLoading(false);
    });
    return () => { active = false; };
  }, [priceListId, priceRatesRetry]);

  useEffect(() => {
    const cid = new URLSearchParams(location.search).get("customer");
    if (cid) setCustomerId(cid);
  }, [location.search]);

  const loadDocument = async () => {
    setDocumentLoading(true); setDocumentError("");
    try {
      const { data } = await http.get(`/invoices/${id}`);
      setDocument(data);
      setCustomerId(data.customer_id); setPosCode(data.place_of_supply_code); setPosName(data.place_of_supply);
      setInvDate(data.invoice_date); setDueDate(data.due_date);
      setItems(data.items.map((it) => ({ ...newItem(), ...it })));
      setShipping(data.shipping ?? 0); setOther(data.other_charges ?? 0);
      const savedGrandTotal = Number(data.grand_total);
      const legacyRoundOff = !(Number(data.round_off) === 0 &&
        Number.isFinite(savedGrandTotal) &&
        Math.abs(savedGrandTotal - Math.round(savedGrandTotal)) > 1e-9);
      setRoundOff(data.round_off_enabled != null ? Boolean(data.round_off_enabled) : legacyRoundOff);
      setReceived(data.amount_received ?? 0);
      setPayMode(data.payment_mode || "Cash");
      setTcs(data.tcs_pct ?? 0);
      setTds(data.tds_pct ?? 0);
      setNotes(data.notes || "");
      const savedSelection = data.bank_id != null && data.bank_id !== ""
        ? String(data.bank_id)
        : (data.bank_details ? "__saved__" : "__saved_none__");
      setBankId(savedSelection);
      setOriginalBankSelection(savedSelection);
    } catch (e) { setDocumentError(requestErrorMessage(e, "load invoice")); }
    finally { setDocumentLoading(false); }
  };
  useEffect(() => { if (editing) loadDocument(); }, [id]);

  useEffect(() => {
    const c = customers.find((x) => x.id === customerId);
    if (c && !editing) { setPosCode(c.state_code); setPosName(c.state); }
  }, [customerId, customers]);

  const t = useMemo(() => computeTotals(items, posCode, {
    shipping, other_charges: other, tcs_pct: tcs, tds_pct: tds, round_off_enabled: roundOff,
  }), [items, posCode, shipping, other, tcs, tds, roundOff]);

  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const pickProduct = (i, pid) => {
    if (priceListId && (priceRatesLoading || priceRatesError)) {
      toast.error(priceRatesError || "Wait for the selected price list rates to load, or deselect the price list.");
      return;
    }
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const plRate = plRates[pid];
    setItem(i, { product_id: p.id, name: p.name, hsn: p.hsn, batch_no: p.batch_no, expiry_date: p.expiry_date, unit: p.unit, rate: plRate != null ? plRate : (p.ptr || p.selling_price || p.mrp), mrp: p.mrp, gst_rate: p.gst_rate });
  };
  const addRow = () => setItems((a) => [...a, newItem()]);
  const removeRow = (i) => setItems((a) => a.length > 1 ? a.filter((_, idx) => idx !== i) : a);

  const scanAdd = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scan.trim();
    if (!code) return;
    if (priceListId && (priceRatesLoading || priceRatesError)) {
      toast.error(priceRatesError || "Wait for the selected price list rates to load, or deselect the price list.");
      return;
    }
    setScan("");
    try {
      const { data: p } = await http.get("/pos/scan", { params: { code } });
      const rate = plRates[p.id] != null ? plRates[p.id] : (p.ptr || p.selling_price || p.mrp || 0);
      const line = { product_id: p.id, name: p.name, hsn: p.hsn || "", batch_no: p.batch_no || "",
        expiry_date: p.expiry_date || "", unit: p.unit || "PCS", qty: 1, free_qty: 0,
        rate, mrp: p.mrp || 0, discount_pct: 0, gst_rate: p.gst_rate || 12 };
      setItems((a) => {
        const i = a.findIndex((it) => !it.name && !it.product_id);
        return i >= 0 ? a.map((it, idx) => idx === i ? line : it) : [...a, line];
      });
      toast.success(`Added ${p.name}`);
    } catch (e) { toast.error(requestErrorMessage(e, `find product for code "${code}"`)); }
  };

  const buildPayload = (asDraft = false) => ({
    customer_id: customerId, invoice_date: invDate, due_date: dueDate,
    place_of_supply: posName, place_of_supply_code: posCode,
    items: items.filter((it) => it.name && it.qty > 0),
    shipping: +shipping, other_charges: +other, tcs_pct: +tcs, tds_pct: +tds,
    round_off_enabled: roundOff, amount_received: +received, payment_mode: payMode,
    notes, ...(!editing || (document && bankId !== originalBankSelection)
      ? { bank_id: bankId === "__none__" || bankId === "__saved_none__" || bankId === "__saved__" ? null : bankId }
      : {}),
    status: asDraft ? "draft" : null,
  });

  const save = async (again = false, asDraft = false) => {
    if (!editing && settingsLoading) return toast.error("Wait for company settings to load before saving.");
    if (!editing && settingsError) return toast.error(settingsError);
    if (priceListId && (priceRatesLoading || priceRatesError)) {
      return toast.error(priceRatesError || "Wait for selected price list rates to load, or deselect the price list.");
    }
    if (editing && !document) return;
    if (!customerId) return toast.error("Select a customer");
    const valid = items.filter((it) => it.name && it.qty > 0);
    if (valid.length === 0) return toast.error("Add at least one product");
    setSaving(true);
    try {
      const payload = buildPayload(asDraft);
      let res;
      if (editing) res = await http.put(`/invoices/${id}`, payload);
      else res = await http.post("/invoices", payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      if (again) {
        setItems([newItem()]); setReceived(0); setNotes("");
      } else navigate(`/sales/invoices/${res.data.id}`);
    } catch (e) { toast.error(requestErrorMessage(e, editing ? "save invoice" : "create invoice")); }
    finally { setSaving(false); }
  };

  const STATES = [["36", "Telangana"], ["37", "Andhra Pradesh"], ["29", "Karnataka"], ["27", "Maharashtra"], ["33", "Tamil Nadu"], ["07", "Delhi"], ["24", "Gujarat"], ["09", "Uttar Pradesh"], ["19", "West Bengal"], ["32", "Kerala"]];
  const documentText = resolveDocumentText(company, document, "sales_invoice");
  const banks = company.banks || [];
  const savedBankId = document?.bank_id != null && document.bank_id !== "" ? String(document.bank_id) : null;
  const showSavedBank = editing && document && (
    bankId === "__saved__" ||
    (savedBankId && !banks.some((bank) => String(bank.id) === savedBankId))
  );
  const savedBankOptionValue = bankId === "__saved__" ? "__saved__" : savedBankId;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl">
      <button onClick={() => navigate("/sales/invoices")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{editing ? "Edit Invoice" : "Create Sales Invoice"}</h1>
      {(prereqError || settingsError || documentError) && <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        {prereqError && <div className="flex items-center justify-between gap-3"><span>{prereqError}</span><Button size="sm" variant="outline" onClick={loadPrerequisites}>Retry prerequisite data</Button></div>}
        {settingsError && <div className="flex items-center justify-between gap-3"><span>{settingsError}</span><Button size="sm" variant="outline" onClick={loadSettings}>Retry company settings</Button></div>}
        {documentError && <div className="flex items-center justify-between gap-3"><span>{documentError}</span><Button size="sm" variant="outline" onClick={loadDocument}>Retry invoice</Button></div>}
      </div>}
      {prereqLoading && <div className="text-sm text-muted-foreground">Loading customers and products…</div>}
      {settingsLoading && <div className="text-sm text-muted-foreground">Loading company settings…</div>}
      {documentLoading && editing && <div className="text-sm text-muted-foreground">Loading invoice…</div>}

      <Card className="p-5 sm:p-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <Label htmlFor="invoice-customer-select">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger id="invoice-customer-select" className="mt-1.5 h-11" data-testid="invoice-customer-select"><SelectValue placeholder="Search customer…" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.gstin ? `· ${c.gstin}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="invoice-pos-select">Place of Supply</Label>
            <Select value={posCode} onValueChange={(v) => { setPosCode(v); setPosName(STATES.find((s) => s[0] === v)?.[1] || ""); }}>
              <SelectTrigger id="invoice-pos-select" className="mt-1.5 h-11" data-testid="invoice-pos-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATES.map((s) => <SelectItem key={s[0]} value={s[0]}>{s[0]} — {s[1]}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-xs mt-1.5 text-muted-foreground">{t.intra ? "Intra-state · CGST + SGST" : "Inter-state · IGST"}</div>
          </div>
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-2">
            <div><Label htmlFor="invoice-date-input">Inv Date</Label><Input id="invoice-date-input" type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} className="mt-1.5 h-11 w-full" data-testid="invoice-date-input" /></div>
            <div><Label htmlFor="invoice-due-date">Due Date</Label><Input id="invoice-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5 h-11 w-full" /></div>
          </div>
          <div className="lg:col-span-2">
            <Label htmlFor="invoice-bank-select">Bank for this invoice</Label>
            <Select value={bankId} onValueChange={setBankId}>
              <SelectTrigger id="invoice-bank-select" className="mt-1.5 h-11" data-testid="invoice-bank-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {showSavedBank && <SelectItem value={savedBankOptionValue}>{savedBankLabel(document.bank_details)}</SelectItem>}
                {editing && originalBankSelection === "__saved_none__" && <SelectItem value="__saved_none__">No bank (unchanged)</SelectItem>}
                <SelectItem value="__none__">No bank</SelectItem>
                {banks.map((bank, index) => <SelectItem key={bank.id || index} value={String(bank.id)}>{bank.name || bank.bank_name || "Bank"}{bank.account_number || bank.bank_account ? ` · ${bank.account_number || bank.bank_account}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
          <div className="p-3 sm:p-4 border-b flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading font-semibold">Items</h3>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative">
              <ScanLine className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-600" />
              <Input aria-label="Scan barcode" placeholder="Scan barcode…" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={scanAdd} className="pl-8 h-11 w-[min(44vw,11rem)] sm:h-9 sm:w-44" data-testid="invoice-scan-input" />
            </div>
            {priceLists.length > 0 && (
              <Select value={priceListId || "__none"} onValueChange={(v) => setPriceListId(v === "__none" ? "" : v)}>
                <SelectTrigger className="h-11 w-[min(44vw,10rem)] sm:h-9 sm:w-40" data-testid="invoice-pricelist-select"><SelectValue placeholder="Price List" /></SelectTrigger>
                <SelectContent><SelectItem value="__none">Default price</SelectItem>{priceLists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {priceListId && priceRatesError && <div className="flex w-full items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
              <span>{priceRatesError} Deselect the price list to use default rates.</span>
              <Button size="sm" variant="outline" onClick={() => setPriceRatesRetry((n) => n + 1)}>Retry</Button>
            </div>}
            {priceListId && priceRatesLoading && <span className="text-xs text-muted-foreground">Loading price list rates…</span>}
            {cfg.Gear}
            {cfg.FieldsButton}
            <Button size="sm" variant="outline" onClick={addRow} className="min-h-11 sm:min-h-9" data-testid="add-item-button"><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
          </div>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium min-w-[200px]">Product</th>
                {cfg.show("hsn") && <th className="px-2 py-2 font-medium">HSN</th>}
                {cfg.show("batch_no") && <th className="px-2 py-2 font-medium">Batch</th>}
                {cfg.show("expiry_date") && <th className="px-2 py-2 font-medium">Expiry</th>}
                {cfg.customHeaders()}
                {cfg.show("qty") && <th className="px-2 py-2 font-medium text-right">Qty</th>}
                {cfg.show("free_qty") && <th className="px-2 py-2 font-medium text-right">Free</th>}
                {cfg.show("rate") && <th className="px-2 py-2 font-medium text-right">Rate</th>}
                {cfg.show("discount_pct") && <th className="px-2 py-2 font-medium text-right">Disc%</th>}
                {cfg.show("gst_rate") && <th className="px-2 py-2 font-medium text-right">GST%</th>}
                <th className="px-2 py-2 font-medium text-right">Amount</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t" data-testid={`item-row-${i}`}>
                  <td className="px-3 py-2">
                    <Select value={it.product_id} disabled={Boolean(priceListId && (priceRatesLoading || priceRatesError))} onValueChange={(v) => pickProduct(i, v)}>
                      <SelectTrigger className="h-9" data-testid={`item-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.batch_no} · stk {p.stock}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  {cfg.show("hsn") && <td className="px-2 py-2"><Input value={it.hsn} onChange={(e) => setItem(i, { hsn: e.target.value })} className="h-9 w-20 font-mono text-xs" /></td>}
                  {cfg.show("batch_no") && <td className="px-2 py-2"><Input value={it.batch_no} onChange={(e) => setItem(i, { batch_no: e.target.value })} className="h-9 w-20 font-mono text-xs" /></td>}
                  {cfg.show("expiry_date") && <td className="px-2 py-2"><Input value={it.expiry_date} onChange={(e) => setItem(i, { expiry_date: e.target.value })} className="h-9 w-16 font-mono text-xs" /></td>}
                  {cfg.customCells(it, (patch) => setItem(i, patch))}
                  {cfg.show("qty") && <td className="px-2 py-2"><Input type="number" value={it.qty} onChange={(e) => setItem(i, { qty: +e.target.value })} className="h-9 w-16 text-right" data-testid={`item-qty-${i}`} /></td>}
                  {cfg.show("free_qty") && <td className="px-2 py-2"><Input type="number" value={it.free_qty} onChange={(e) => setItem(i, { free_qty: +e.target.value })} className="h-9 w-14 text-right" /></td>}
                  {cfg.show("rate") && <td className="px-2 py-2"><Input type="number" value={it.rate} onChange={(e) => setItem(i, { rate: +e.target.value })} className="h-9 w-20 text-right" data-testid={`item-rate-${i}`} /></td>}
                  {cfg.show("discount_pct") && <td className="px-2 py-2"><Input type="number" value={it.discount_pct} onChange={(e) => setItem(i, { discount_pct: +e.target.value })} className="h-9 w-16 text-right" /></td>}
                  {cfg.show("gst_rate") && <td className="px-2 py-2"><Input type="number" value={it.gst_rate} onChange={(e) => setItem(i, { gst_rate: +e.target.value })} className="h-9 w-16 text-right" /></td>}
                  <td className="px-2 py-2 text-right font-mono">{inr(t.rows[i]?.amount || 0)}</td>
                  <td className="px-2 py-2"><button onClick={() => removeRow(i)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`remove-item-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-3 p-3 md:hidden">
          {items.map((it, i) => (
            <section key={i} className="rounded-lg border border-border bg-card p-3 space-y-4" data-testid={`item-card-${i}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Label htmlFor={`invoice-product-mobile-${i}`}>Product</Label>
                  <Select value={it.product_id} disabled={Boolean(priceListId && (priceRatesLoading || priceRatesError))} onValueChange={(v) => pickProduct(i, v)}>
                    <SelectTrigger id={`invoice-product-mobile-${i}`} className="mt-1 h-11" data-testid={`item-product-mobile-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.batch_no} · stk {p.stock}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <button type="button" onClick={() => removeRow(i)} aria-label={`Remove item ${i + 1}`} className="mt-6 grid h-11 w-11 shrink-0 place-items-center rounded-md text-rose-600 hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`remove-item-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {cfg.show("hsn") && <div><Label htmlFor={`invoice-hsn-${i}`} className="text-xs text-muted-foreground">HSN</Label><Input id={`invoice-hsn-${i}`} aria-label={`HSN for item ${i + 1}`} value={it.hsn || ""} onChange={(e) => setItem(i, { hsn: e.target.value })} className="mt-1 h-11 w-full font-mono text-sm" /></div>}
                {cfg.show("batch_no") && <div><Label htmlFor={`invoice-batch-${i}`} className="text-xs text-muted-foreground">Batch</Label><Input id={`invoice-batch-${i}`} aria-label={`Batch for item ${i + 1}`} value={it.batch_no || ""} onChange={(e) => setItem(i, { batch_no: e.target.value })} className="mt-1 h-11 w-full font-mono text-sm" /></div>}
                {cfg.show("expiry_date") && <div><Label htmlFor={`invoice-expiry-${i}`} className="text-xs text-muted-foreground">Expiry</Label><Input id={`invoice-expiry-${i}`} aria-label={`Expiry for item ${i + 1}`} value={it.expiry_date || ""} onChange={(e) => setItem(i, { expiry_date: e.target.value })} className="mt-1 h-11 w-full font-mono text-sm" /></div>}
                {cfg.show("qty") && <div><Label htmlFor={`invoice-qty-mobile-${i}`} className="text-xs text-muted-foreground">Qty</Label><Input id={`invoice-qty-mobile-${i}`} type="number" value={it.qty} onChange={(e) => setItem(i, { qty: +e.target.value })} className="mt-1 h-11 w-full text-right" data-testid={`item-qty-${i}`} /></div>}
                {cfg.show("free_qty") && <div><Label htmlFor={`invoice-free-${i}`} className="text-xs text-muted-foreground">Free Qty</Label><Input id={`invoice-free-${i}`} type="number" value={it.free_qty} onChange={(e) => setItem(i, { free_qty: +e.target.value })} className="mt-1 h-11 w-full text-right" /></div>}
                {cfg.show("rate") && <div><Label htmlFor={`invoice-rate-mobile-${i}`} className="text-xs text-muted-foreground">Rate</Label><Input id={`invoice-rate-mobile-${i}`} type="number" value={it.rate} onChange={(e) => setItem(i, { rate: +e.target.value })} className="mt-1 h-11 w-full text-right" data-testid={`item-rate-${i}`} /></div>}
                {cfg.show("discount_pct") && <div><Label htmlFor={`invoice-discount-${i}`} className="text-xs text-muted-foreground">Discount %</Label><Input id={`invoice-discount-${i}`} type="number" value={it.discount_pct} onChange={(e) => setItem(i, { discount_pct: +e.target.value })} className="mt-1 h-11 w-full text-right" /></div>}
                {cfg.show("gst_rate") && <div><Label htmlFor={`invoice-gst-${i}`} className="text-xs text-muted-foreground">GST %</Label><Input id={`invoice-gst-${i}`} type="number" value={it.gst_rate} onChange={(e) => setItem(i, { gst_rate: +e.target.value })} className="mt-1 h-11 w-full text-right" /></div>}
                {cfg.customMobileFields(it, (patch) => setItem(i, patch), i)}
              </div>
              <div className="flex items-center justify-between border-t pt-3 text-sm">
                <span className="text-muted-foreground">Line amount</span><span className="font-mono font-semibold">{inr(t.rows[i]?.amount || 0)}</span>
              </div>
            </section>
          ))}
        </div>
      </Card>
      {cfg.Modal}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 space-y-4">
          <h3 className="font-heading font-semibold">Charges & Notes</h3>
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
            <div><Label htmlFor="invoice-shipping">Shipping</Label><Input id="invoice-shipping" type="number" value={shipping} onChange={(e) => setShipping(+e.target.value)} className="mt-1.5 h-11" /></div>
            <div><Label htmlFor="invoice-other">Other Charges</Label><Input id="invoice-other" type="number" value={other} onChange={(e) => setOther(+e.target.value)} className="mt-1.5 h-11" /></div>
            <div><Label htmlFor="invoice-tcs">TCS %</Label><Input id="invoice-tcs" type="number" value={tcs} onChange={(e) => setTcs(+e.target.value)} className="mt-1.5 h-11" /></div>
            <div><Label htmlFor="invoice-tds">TDS %</Label><Input id="invoice-tds" type="number" value={tds} onChange={(e) => setTds(+e.target.value)} className="mt-1.5 h-11" /></div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={roundOff} onCheckedChange={setRoundOff} data-testid="roundoff-switch" />
            <Label className="cursor-pointer">Auto Round-Off</Label>
          </div>
          <div><Label htmlFor="invoice-notes">Notes</Label><Input id="invoice-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 h-11" /></div>
          {(documentText.notes || documentText.terms) && <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
            <div className="flex justify-between items-center"><span className="font-medium">Shared document text</span><Link to="/settings" className="text-xs text-brand-600 hover:underline">Settings</Link></div>
            {documentText.notes && <div><div className="text-xs font-medium text-muted-foreground">Notes</div><p className="whitespace-pre-wrap">{documentText.notes}</p></div>}
            {documentText.terms && <div><div className="text-xs font-medium text-muted-foreground">Terms &amp; Conditions</div><p className="whitespace-pre-wrap">{documentText.terms}</p></div>}
          </div>}
        </Card>

        <Card className="p-5">
          <h3 className="font-heading font-semibold mb-3">Summary</h3>
          <div className="space-y-2 text-sm">
            <Row label="Taxable Amount" value={inr(t.subtotal)} />
            <Row label="Total Discount" value={inr(t.totalDiscount)} muted />
            {t.intra ? (<>
              <Row label="CGST" value={inr(t.cgst)} />
              <Row label="SGST" value={inr(t.sgst)} />
            </>) : <Row label="IGST" value={inr(t.igst)} />}
            {shipping > 0 && <Row label="Shipping" value={inr(t.shipping)} />}
            {other > 0 && <Row label="Other Charges" value={inr(t.other)} />}
            {tcs > 0 && <Row label="TCS" value={inr(t.tcs)} />}
            {tds > 0 && <Row label="TDS" value={"-" + inr(t.tds)} />}
            <Row label="Round Off" value={inr(t.roundOff)} muted />
            <div className="flex justify-between pt-2 border-t font-heading text-lg font-bold">
              <span>Grand Total</span><span className="tabular" data-testid="grand-total-amount">{inr(t.grand)}</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
            <div><Label htmlFor="invoice-amount-received">Amount Received</Label><Input id="invoice-amount-received" type="number" value={received} onChange={(e) => setReceived(+e.target.value)} className="mt-1.5 h-11" data-testid="amount-received-input" /></div>
            <div>
              <Label htmlFor="invoice-payment-mode">Payment Mode</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger id="invoice-payment-mode" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank", "UPI", "Cheque", "Card", "NEFT"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between text-sm mt-3 pt-3 border-t">
            <span className="text-muted-foreground">Balance Due</span>
            <span className="font-mono font-semibold text-rose-600">{inr(Math.max(0, t.grand - received))}</span>
          </div>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 sm:justify-end sticky bottom-0 z-10 bg-background/95 backdrop-blur py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <Button variant="outline" className="min-h-11 w-full sm:w-auto" onClick={() => save(false, true)} disabled={saving || prereqLoading || !!prereqError || (!editing && (settingsLoading || !!settingsError)) || Boolean(priceListId && (priceRatesLoading || priceRatesError)) || (editing && (!document || documentLoading || !!documentError))} data-testid="save-draft-button">Save as Draft</Button>
        {!editing && <Button variant="outline" className="min-h-11 w-full sm:w-auto" onClick={() => save(true)} disabled={saving || prereqLoading || !!prereqError || settingsLoading || !!settingsError || Boolean(priceListId && (priceRatesLoading || priceRatesError))} data-testid="save-new-button">Save & New</Button>}
        <Button className="min-h-11 w-full sm:w-auto" onClick={() => save(false)} disabled={saving || prereqLoading || !!prereqError || (!editing && (settingsLoading || !!settingsError)) || Boolean(priceListId && (priceRatesLoading || priceRatesError)) || (editing && (!document || documentLoading || !!documentError))} data-testid="save-invoice-button"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save Invoice"}</Button>
      </div>
    </div>
  );
}

const Row = ({ label, value, muted }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono ${muted ? "text-muted-foreground" : ""}`}>{value}</span>
  </div>
);
